import {ChangeDetectionStrategy, Component, Inject, OnInit, signal, ViewChild} from '@angular/core';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSelect} from '@angular/material/select';
import {MatOption} from '@angular/material/core';
import {finalize, skip, filter, take, tap} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';

import {UserService} from '../../../services/shared/user.service';
import {SnackbarService} from '../../../services/snackbar.service';
import SectionsAccess from 'src/app/permission/enums/sectionsAccess.enum';
import PermissionLevel from 'src/app/permission/enums/permissionLevel.enum';
import {UserPermissionDto} from 'src/app/permission/classes/userPermissionDto.models';
import {UserPermissionDataService} from '../../../services/gp/user-permission-data.service';
import {FinancialPermissionLevel} from 'src/app/permission/enums/financialPermissionLevel.enum';
import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import ContactsVisibilityPermission from 'src/app/permission/enums/contactsVisibilityPermission.enum';
import {UtilsService} from 'src/app/services/utils.service';
import {BaseResponseDto} from 'src/app/shared/models/BaseResponseDto.model';
import {CustomValidators} from 'src/app/shared/validators/custom.validators';
import { DialogService } from 'src/app/services/dialog.service';
import { AlertDialogParams } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { IntegrationStatus } from "src/app/account/my-account/model/OrganizationIntegrationRes.model";

export class UserAndPermissionEditDialogData {
  createMode: boolean;
  userPermissionDto?: UserPermissionDto;
  allHoldings: GpHoldingListItemReqRes[];
}

@Component({
  selector: 'terra-user-and-permission-edit-dialog',
  templateUrl: './user-and-permission-edit-dialog.component.html',
  styleUrls: ['./user-and-permission-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAndPermissionEditDialogComponent extends OnDestroyMixin implements OnInit {
  @ViewChild('selectSectionsAccess') selectSectionsAccess: MatSelect;
  @ViewChild('selectHolding') selectHolding: MatSelect;

  isLoading$ = new BehaviorSubject<boolean>(false);

  pageForm: UntypedFormGroup;
  generalServerError: string;
  failureMessage = 'Operation failed';
  successMessage = 'Permission saved';
  SectionsAccess = SectionsAccess;
  PermissionLevel = PermissionLevel;
  FinancialPermissionLevel = FinancialPermissionLevel;

  ContactsVisibilityPermission = ContactsVisibilityPermission;
  isSubmitting$ = new BehaviorSubject<boolean>(false);

  SectionsAccessList = SectionsAccess.listAll();

  rentManagerIntegrationStatus = signal(0)
  IntegrationStatus = IntegrationStatus;

  constructor(
    private fb: UntypedFormBuilder,
    private snackbarService: SnackbarService,
    private utilsService: UtilsService,
    private userPermissionDataService: UserPermissionDataService,
    private dialogRef: MatDialogRef<UserAndPermissionEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public context: UserAndPermissionEditDialogData,
    private userService: UserService,
    private dialogService: DialogService
  ) {
    super();
    dialogRef.addPanelClass('terra-user-and-permission-edit-dialog');
  }

  ngOnInit(): void {
    this.generateForm();

    if (this.context.createMode) {
      this.pageForm.addControl('email', new UntypedFormControl(this.context.userPermissionDto.email, Validators.required));
      this.populateForm(this.context.userPermissionDto);
    } else {
      this.initialDialogForEditMode(this.context.userPermissionDto.id);
    }

    this.pageForm.get('permissionLevel').valueChanges.pipe(
      untilComponentDestroyed(this)
    )
    .subscribe(
      permissionLevel => {
        if (permissionLevel === PermissionLevel.Admin){
          this.setAdminPermission();
        }
        else{
          this.disableFormForAdmin();
          this.changeFinancialLevelToNonSignatoryForReadOnly();
        }
      }
    );

    this.userService.accountDetails$.pipe(take(1)).subscribe(clientDetails => {
      const status = clientDetails?.organizationDetails.integrations?.find(integration => integration.name === 'RentManager')?.status;
      this.rentManagerIntegrationStatus.set(status);
    })

  }

  setAdminPermission(){
    this.populateAdminForm(this.adminPermission);
  }

  disableFormForAdmin(){
    if (this.pageForm.get('permissionLevel').value === PermissionLevel.Admin){
      this.pageForm.disable({emitEvent: false});
      this.pageForm.get('name').enable({emitEvent: false});
      this.pageForm.get('permissionLevel').enable({emitEvent: false});
      if (this.context.createMode){
        this.pageForm.get('email').enable({emitEvent: false});
      }
    }
    else{
      this.pageForm.enable({emitEvent: false});
    }
  }

  private get adminPermission(): UserPermissionDto{
    const adminPermission = new UserPermissionDto(0);
    adminPermission.permissionLevel = PermissionLevel.Admin;
    adminPermission.financialPermission = FinancialPermissionLevel.AuthorizedSignatory;
    adminPermission.contactsVisibility = ContactsVisibilityPermission.Visible;
    adminPermission.allowedSections = SectionsAccess.All;
    adminPermission.holdings = [...this.context.allHoldings];

    return adminPermission;
  }

  private initialDialogForEditMode(userPermissionId: number) {
    this.isLoading$.next(true);
    this.userPermissionDataService.getById(userPermissionId)
      .pipe(
        take(1),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe(
        userPermission => this.populateForm(userPermission),
        error => {
          this.dialogRef.close();
          if (error instanceof BaseResponseDto) {
            this.utilsService.alertErrorMessage(error);
          } else {
            this.utilsService.alertErrorMessage(new BaseResponseDto());
          }
        }
      );
  }

  private initHoldings(userPermission: UserPermissionDto) {
    const holdingsIds = userPermission.holdings.map(h => h.id);
    if (holdingsIds.length === this.context.allHoldings.length) {
      holdingsIds.unshift(-1);
    }
    return holdingsIds;
  }

  private generateForm() {
    this.pageForm = this.fb.group({
      name: ['', Validators.required],
      permissionLevel: [null, Validators.required],
      sectionsAccess: [[]],
      financialPermissionLevel: [null, Validators.required],
      holdings: [[]],
      contactVisibilityPermission: [null, Validators.required]
    });

    if (this.context.createMode) {
      this.pageForm.addControl('email', new UntypedFormControl('', [Validators.required, CustomValidators.EmailWithSpaces]));
    }

    this.pageForm.get('contactVisibilityPermission').valueChanges.pipe(
      untilComponentDestroyed(this),
      // skip the default/populated value
      skip(1)
    ).subscribe((contactVisibility: ContactsVisibilityPermission) => {
      switch (contactVisibility) {
        case ContactsVisibilityPermission.Hidden:
          this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.Contacts).deselect();
          break;
        case ContactsVisibilityPermission.Visible:
          this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.Contacts).select();
          break;
      }

      this.optionClickSectionsAccess();
    });

    this.pageForm.get('financialPermissionLevel').valueChanges.pipe(
      untilComponentDestroyed(this),
      // skip the default/populated value
      skip(1)
    ).subscribe((financialPermissionLevel: FinancialPermissionLevel) => {
      switch (financialPermissionLevel) {
        case FinancialPermissionLevel.Hidden:
          this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.BankAccounts).deselect();
          break;
        case FinancialPermissionLevel.AuthorizedSignatory:
        case FinancialPermissionLevel.NonSignatory:
          this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.BankAccounts).select();
          break;
      }

      this.optionClickSectionsAccess();
    });
  }

  private populateForm(userPermission: UserPermissionDto) {
    this.pageForm.patchValue({
      name: userPermission.name,
      permissionLevel: userPermission.permissionLevel,
      sectionsAccess: this.SectionsAccessList.filter(flag => SectionsAccess.isOn(userPermission.allowedSections, flag)),
      financialPermissionLevel: userPermission.financialPermission,
      holdings: this.initHoldings(userPermission),
      contactVisibilityPermission: userPermission.contactsVisibility
    });

    this.optionClickHolding();
    this.disableFormForAdmin();
    this.changeFinancialLevelToNonSignatoryForReadOnly()
  }

  private changeFinancialLevelToNonSignatoryForReadOnly() {
    if(this.pageForm.get("permissionLevel").value === PermissionLevel.Readonly && this.pageForm.get("financialPermissionLevel").value === FinancialPermissionLevel.AuthorizedSignatory) {
      this.pageForm.patchValue({financialPermissionLevel: FinancialPermissionLevel.NonSignatory});
    }
  }

  private populateAdminForm(userPermission: UserPermissionDto) {
    this.pageForm.patchValue({
      sectionsAccess: this.SectionsAccessList.filter(flag => SectionsAccess.isOn(userPermission.allowedSections, flag)),
      financialPermissionLevel: userPermission.financialPermission,
      holdings: this.initHoldings(userPermission),
      contactVisibilityPermission: userPermission.contactsVisibility
    });

    this.optionClickHolding();
    this.disableFormForAdmin();
  }

  save() {
    if (this.pageForm.invalid) {
      return;
    }

    this.isSubmitting$.next(true);

    const model = this.generateSubmitModel();

    if (this.context.createMode) {
      model.email = this.pageForm.get('email').value;

      this.userService.createUserPermission(model)
        .pipe(
          untilComponentDestroyed(this),
          take(1),
          finalize(() => this.isSubmitting$.next(false))
        )
        .subscribe(
          data => this.successRequest(data),
          error => this.failedRequest(error)
        );
    } else {
      model.email = this.context.userPermissionDto.email;
      model.clientId = this.context.userPermissionDto.clientId;

      const dialogParams = new AlertDialogParams();
      dialogParams.title = "Notice";
      dialogParams.description = "The new permissions will be applied immediately. <br>For a better user experience the user should refresh the platform page.";

      this.userPermissionDataService.updateUserPermission(model)
        .pipe(
          take(1),
          tap(_ => this.dialogService.alertDialog(dialogParams)),
          finalize(() => this.isSubmitting$.next(false))
        ).subscribe(
        data => this.successRequest(data),
        error => this.failedRequest(error)
      );
    }
  }

  private generateSubmitModel(): UserPermissionDto {
    const formValues = this.pageForm.getRawValue();
    const model = new UserPermissionDto(this.context.userPermissionDto.id);

    model.name = formValues.name;
    model.permissionLevel = formValues.permissionLevel;
    model.allowedSections = SectionsAccess.convertToFlags(formValues.sectionsAccess);
    model.financialPermission = formValues.financialPermissionLevel;
    model.holdings = this.context.allHoldings.filter(h => formValues.holdings.includes(h.id));
    model.comment = formValues.comment;
    model.contactsVisibility = formValues.contactVisibilityPermission;
    return model;
  }

  private successRequest(response: UserPermissionDto) {
    this.snackbarService.showGeneralMessage(`User ${this.context.createMode ? 'invited' : 'updated'}`);
    this.dialogRef.close(response);
  }

  private failedRequest(error: any) {
    if (error instanceof BaseResponseDto) {
      this.utilsService.alertErrorMessage(error);
    } else {
      this.snackbarService.showGeneralMessage(`Failed to ${this.context.createMode ? 'create new user' : 'update user permission'}`);
    }
  }

  toggleAllSelectionSectionsAccess() {
    const selectedSections = this.pageForm.get('sectionsAccess').value as Array<SectionsAccess>;
    const isAllSelected = selectedSections.includes(SectionsAccess.All);
    if (isAllSelected) {
      this.selectSectionsAccess.options.filter((item: MatOption) => item.value !== SectionsAccess.Contacts && item.value !== SectionsAccess.BankAccounts)
        .forEach((item: MatOption) => item.select());
    } else {
      this.selectSectionsAccess.options.filter((item: MatOption) => item.value !== SectionsAccess.Contacts && item.value !== SectionsAccess.BankAccounts)
        .forEach((item: MatOption) => item.deselect());
    }

    const isContactsSelected = selectedSections.includes(SectionsAccess.Contacts);
    const isBankAccountsSelected = selectedSections.includes(SectionsAccess.BankAccounts);
    if (isAllSelected && (!isContactsSelected || !isBankAccountsSelected)) {
      this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.All).deselect();
    }
  }

  optionClickSectionsAccess() {
    const sectionsAccessArray = this.pageForm.get('sectionsAccess').value as Array<SectionsAccess>;
    if (sectionsAccessArray.length !== this.SectionsAccessList.length && sectionsAccessArray.indexOf(SectionsAccess.All) !== -1) {
      this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.All).deselect();
    }
    if (sectionsAccessArray.length === (this.SectionsAccessList.length - 1) && sectionsAccessArray.indexOf(SectionsAccess.All) === -1) {
      this.selectSectionsAccess.options.find((item: MatOption) => item.value === SectionsAccess.All).select();
    }
  }

  get includeAllSectionsAccess(): boolean {
    const sectionsAccessArray = this.pageForm.get('sectionsAccess').value as Array<number> || [];
    return sectionsAccessArray.indexOf(SectionsAccess.All) !== -1;
  }

  toggleAllSelectionHolding() {
    if ((this.pageForm.get('holdings').value as Array<SectionsAccess>).includes(-1)) {
      this.selectHolding.options.forEach((item: MatOption) => item.select());
    } else {
      this.selectHolding.options.forEach((item: MatOption) => item.deselect());
    }
  }

  optionClickHolding() {
    const holdingsArray = this.pageForm.get('holdings').value as Array<number>;
    if (holdingsArray.length <= this.context.allHoldings.length && holdingsArray.indexOf(-1) !== -1) {
      this.selectHolding.options.find((item: MatOption) => item.value === -1).deselect();
    }
    if (holdingsArray.length >= this.context.allHoldings.length && holdingsArray.indexOf(-1) === -1) {
      this.selectHolding.options.find((item: MatOption) => item.value === -1).select();
    }
  }

  getNameOfHolding(holdingId: number) {
    return this.context.allHoldings.find(holding => holding.id === holdingId)?.name || '';
  }

  get includeAllHolding(): boolean {
    const holdingsArray = this.pageForm.get('holdings').value as Array<number> || [];
    return holdingsArray.indexOf(-1) !== -1;
  }
  
  get showRentManagerMessage(): boolean {
    return [IntegrationStatus.On, IntegrationStatus.Displayed].includes(this.rentManagerIntegrationStatus()) && !this.includeAllHolding;
  }
}
